import Countdown from "react-countdown";
import { AUCTION_STATUS_MAP } from "../LandingPagev2";
import styles from "./AuctionTable.module.css";
import { useAppContext } from "../../../../Context/AppContext";

const formatDateTime = (inputDate) => {
  const date = new Date(inputDate);
  const pad = (n) => (n < 10 ? `0${n}` : n);

  const dd = pad(date.getDate());
  const mm = pad(date.getMonth() + 1);
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());

  return `${dd}-${mm}-${yyyy} : ${hh}:${min}`;
};

export const timerRenderer = ({ days, hours, minutes, seconds, completed }) => {
  if (days > 0) {
    return (
      <span className="text-danger font-weight-bold underline">
        {days}d {hours}h {minutes}m {seconds}s
      </span>
    );
  }

  return (
    <span className="text-danger font-weight-bold">
      {hours}h {minutes}m {seconds}s
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const normalized = status?.toLowerCase();
  let badgeClass = styles.statusNeutral;

  if (normalized === "active") badgeClass = styles.statusActive;
  else if (normalized === "inactive") badgeClass = styles.statusInactive;
  else if (normalized === "closed") badgeClass = styles.statusClosed;
  else if (normalized === "paused") badgeClass = styles.statusPaused;

  return (
    <span className={`${styles.statusBadge} ${badgeClass}`}>
      {AUCTION_STATUS_MAP[status] ?? status}
    </span>
  );
};

export default function AuctionTable({
  auctions = [],
  isLoading,
  error,
  bucket,
  refetch,
  onNavigateAuction,
}) {
  const { transporterInfo } = useAppContext();

  if (error) {
    return <div className={styles.loading}>Something went wrong!</div>;
  }

  if (isLoading) {
    return <div className={styles.loading}>Loading auctions...</div>;
  }

  if (!auctions.length) {
    return (
      <div className={styles.noData}>No auctions found for this bucket.</div>
    );
  }

  return (
    <table className={styles.auctionsListTable}>
      <thead>
        <tr>
          <th>#</th>
          <th>ID</th>
          <th>Lane/Lot</th>
          <th>Company Name</th>
          <th>Procurement</th>
          <th className="">Status</th>
          {bucket === "inactive" && <th className="text-center">Start Time</th>}
          {/* {bucket === "inactive" && <th className="text-center">Starts In</th>} */}
          {bucket === "active" && (
            <th className="text-center">Time Remaining</th>
          )}
          {bucket === "closed" && <th className="text-center">Closed At</th>}
          {/* {(bucket === "active" || bucket === "closed") && ( */}
          {bucket === "closed" && <th className="text-center"></th>}
          {bucket !== "inactive" && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {auctions.map((auction, index) => (
          <tr
            key={auction.id}
            className={
              auction.auction_status !== "inactive" ? styles.clickableRow : ""
            }
            onClick={() => onNavigateAuction(auction)}
          >
            <td className="text-muted">{index + 1}</td>
            <td className="text-muted" style={{ maxWidth: "100px" }}>
              {auction?.display_id}
            </td>
            <td style={{ maxWidth: "180px" }}>
              {auction?.auction_description}
            </td>
            <td style={{ maxWidth: "200px" }}>
              {auction?.company_name ?? "NA"}
            </td>
            <td style={{ maxWidth: "200px" }}>
              {auction.procurement_name ?? "NA"}
            </td>
            <td className="">
              <StatusBadge status={auction.auction_status} />
            </td>
            {bucket === "inactive" && (
              <td className="text-center">
                {formatDateTime(auction.start_timestamp)}
              </td>
            )}
            {/* {bucket === "inactive" && (
              <td className="text-center">
                <Countdown
                  date={auction.start_timestamp || new Date()}
                  renderer={timerRenderer}
                />
              </td>
            )} */}
            {bucket === "active" && (
              <td className="text-center">
                <div
                  className="d-flex justify-content-center"
                  style={{ gap: "10px" }}
                >
                  <Countdown
                    date={auction.closed_at_db || new Date()}
                    renderer={timerRenderer}
                    onComplete={refetch}
                  />
                  {auction.auction_status === "active" && (
                    <div className={styles.liveRipple}>
                      <div
                        className={styles.pulseCircle}
                        aria-label="Auction is Live"
                      />
                    </div>
                  )}
                </div>
              </td>
            )}
            {bucket === "closed" && (
              <td
                className="text-center text-muted"
                style={{ fontSize: "11px" }}
              >
                {formatDateTime(auction.close_timestamp)}
              </td>
            )}

            {bucket === "closed" && (
              <td className="text-center">
                {/* Lane Breakup Indicator */}
                {auction?.lot_no && (
                  <div>
                    <div
                      className={`${styles.laneBreakupLabel} ${
                        auction?.freight_breakup_submission_status === "active"
                          ? styles.activeFreightBreakup
                          : auction?.freight_breakup_submission_status ===
                            "closed"
                          ? styles.closedFreightBreakup
                          : ""
                      }`}
                      title={
                        auction?.freight_breakup_submission_status === "active"
                          ? "Freight Breakup Submission Active"
                          : auction?.freight_breakup_submission_status ===
                            "closed"
                          ? "Freight Breakup Submission Closed"
                          : "Freight Breakup Submission Pending"
                      }
                    >
                      <i className="fa fa-code-fork mx-1" aria-hidden="true" />
                      Lot Auction
                    </div>
                    {auction?.freight_breakup_submission_status ===
                      "active" && (
                      <div className={styles.countdownWrapper}>
                        <Countdown
                          date={auction?.freight_breakup_submission_timestamp}
                          renderer={timerRenderer}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Client Responded or Advanced Label */}
                {auction?.type === "advanced" &&
                  (() => {
                    const negotiation = auction?.negotiation_list?.find(
                      (n) =>
                        n.transporter_id === transporterInfo?.transporter_id
                    );

                    const bidHistory = negotiation?.bid_history || [];
                    const lastEntry = bidHistory[bidHistory.length - 1];

                    const isClientLast =
                      lastEntry?.negotiated_by &&
                      lastEntry.negotiated_by.toLowerCase() !== "transporter";

                    const negotiationOngoing =
                      auction?.post_auction_negotiation === "yes" &&
                      auction?.auction_status === "closed";

                    if (
                      negotiation &&
                      bidHistory.length > 0 &&
                      isClientLast &&
                      negotiationOngoing
                    ) {
                      return (
                        <div className={styles.clientPulseWrapper}>
                          <span className={styles.pulseText}>
                            Client Responded
                          </span>
                          <div className={styles.pulseCircle}></div>
                        </div>
                      );
                    }

                    // Fallback: Show ADVANCED if not showing "Client Responded"
                    return (
                      <span
                        className={styles.advancedLabel}
                        title="Advanced Auction"
                      >
                        ADVANCED AUCTION
                      </span>
                    );
                  })()}
              </td>
            )}

            {bucket !== "inactive" && (
              <td>
                <button
                  onClick={() => onNavigateAuction(auction)}
                  className={styles.actionButton}
                >
                  View
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
